import React from "react";

/**
 * Base block skeleton — uses your tokens (bg-muted) + animate-pulse.
 * Works standalone, and also exposes compound helpers:
 *   <Skeleton.Text lines={3} />
 *   <Skeleton.Circle size={40} />
 *   <Skeleton.Rect width={160} height={24} />
 */
function BaseSkeleton({
  className = "",
  rounded = "md", // "none" | "sm" | "md" | "lg" | "xl" | "full"
  style,
  "aria-label": ariaLabel = "Loading",
}) {
  const roundedMap = {
    none: "rounded-none",
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  };

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      className={`animate-pulse bg-muted ${
        roundedMap[rounded] || "rounded-md"
      } ${className}`}
      style={style}
    />
  );
}

// Text lines (auto varies widths for a nice rhythm)
function Text({ lines = 3, lineHeight = 16, gap = 8, className = "" }) {
  const arr = Array.from({ length: Math.max(1, lines) });
  return (
    <div className={`flex flex-col ${className}`} style={{ gap }}>
      {arr.map((_, i) => {
        // last line slightly shorter
        const widthPct = i === arr.length - 1 ? 62 : 100 - Math.min(30, i * 8); // e.g. 100, 92, 84, 76...
        return (
          <BaseSkeleton
            key={i}
            className="w-full"
            style={{ width: `${widthPct}%`, height: lineHeight }}
            rounded="md"
            aria-label="Loading text"
          />
        );
      })}
    </div>
  );
}

// Circle / avatar / icon
function Circle({ size = 32, className = "" }) {
  return (
    <BaseSkeleton
      className={className}
      rounded="full"
      style={{ width: size, height: size }}
      aria-label="Loading avatar"
    />
  );
}

// Arbitrary rectangle (cards, buttons, chips)
function Rect({ width = 120, height = 24, rounded = "md", className = "" }) {
  return (
    <BaseSkeleton
      className={className}
      rounded={rounded}
      style={{ width, height }}
      aria-label="Loading"
    />
  );
}

const Skeleton = Object.assign(BaseSkeleton, {
  Text,
  Circle,
  Rect,
});

export default Skeleton;
